/**
 * /cash-flow — Statement of Cash Flows (indirect method).
 *
 * Standard CPA layout:
 *   Operating activities    Net income → adjustments (depreciation) →
 *                           working-capital changes
 *   Investing activities    Capital expenditures + asset disposals
 *   Financing activities    Debt draws/payments + owner contributions/
 *                           distributions
 *   ─────────────────────────────────────────────────────
 *   Net change in cash      sum of three sections
 *   Cash at beginning       BS as-of (period_from − 1 day)
 *   Cash at end (computed)  beginning + net change
 *   Cash at end per BS      BS as-of period_to
 *   Reconciliation flag     within $0.01 → ✓; otherwise yellow warning
 *
 * Default range = year-to-date. Same date controls + quick-picks as IS.
 *
 * Multi-currency: all amounts are in the entity base currency (the
 * underlying journal_lines.base_debit/credit columns are FX-converted
 * at posting time).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { cashFlowQueryOptions } from "@/lib/queries";
import { fmtMoney } from "@/components/FinancialStatementSection";
import type { CashFlowSection } from "@/lib/types";

export const Route = createFileRoute("/_auth/cash-flow")({
  component: CashFlowPage,
});


function isoDate(d: Date): string { return d.toISOString().slice(0, 10); }
function startOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfQuarter(d: Date): Date {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
}
function startOfYear(d: Date): Date { return new Date(d.getFullYear(), 0, 1); }


function CashFlowPage() {
  const today = new Date();
  const [periodFrom, setPeriodFrom] = useState(isoDate(startOfYear(today)));
  const [periodTo, setPeriodTo] = useState(isoDate(today));

  const cfQuery = useQuery(cashFlowQueryOptions(periodFrom, periodTo));
  const stmt = cfQuery.data;

  const quickPicks = useMemo(() => {
    const t = new Date();
    const lastMonth = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    const lastYear = new Date(t.getFullYear() - 1, 0, 1);
    return [
      { label: "This month", from: isoDate(startOfMonth(t)), to: isoDate(t) },
      { label: "Last month",
        from: isoDate(startOfMonth(lastMonth)), to: isoDate(endOfMonth(lastMonth)) },
      { label: "This quarter", from: isoDate(startOfQuarter(t)), to: isoDate(t) },
      { label: "YTD", from: isoDate(startOfYear(t)), to: isoDate(t) },
      { label: "Last year", from: isoDate(lastYear),
        to: isoDate(new Date(lastYear.getFullYear(), 11, 31)) },
    ];
  }, []);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header / controls */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 print:hidden dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Statement of Cash Flows
          </h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Indirect method · operating + investing + financing
          </span>
          <div className="flex-1" />
          {cfQuery.isFetching && (
            <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
          )}
        </div>

        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            From
            <input type="date" value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1 text-sm dark:border-gray-700" />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            To
            <input type="date" value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1 text-sm dark:border-gray-700" />
          </label>
          <div className="flex-1" />
          {quickPicks.map((q) => (
            <button key={q.label}
              onClick={() => { setPeriodFrom(q.from); setPeriodTo(q.to); }}
              className="text-xs text-blue-700 hover:underline">
              {q.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto px-6 py-4 print:overflow-visible">
        {!stmt ? (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {cfQuery.isLoading ? "Loading…" : "No data"}
          </div>
        ) : (
          <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-md p-6 print:border-0 print:p-0 dark:bg-gray-900 dark:border-gray-800">
            <div className="border-b border-slate-200 pb-3 mb-4 text-center dark:border-gray-800">
              <div className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Statement of Cash Flows
              </div>
              <div className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">
                {stmt.period_from} → {stmt.period_to}
              </div>
            </div>

            <SectionBlock section={stmt.operating} />
            <SectionBlock section={stmt.investing} />
            <SectionBlock section={stmt.financing} />

            {/* Net change + reconciliation */}
            <div className="border-t-2 border-slate-400 mt-4 pt-3 space-y-1 text-sm">
              <div className="flex justify-between font-semibold">
                <span>Net change in cash</span>
                <span className={
                  "font-mono " +
                  (Number(stmt.net_change_in_cash) >= 0 ? "text-emerald-700" : "text-red-700")
                }>
                  {fmtMoney(stmt.net_change_in_cash)}
                </span>
              </div>
              <div className="flex justify-between text-slate-700 dark:text-slate-300">
                <span>Cash at beginning of period</span>
                <span className="font-mono">{fmtMoney(stmt.cash_at_beginning)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t border-slate-200 pt-1 dark:border-gray-800">
                <span>Cash at end of period (computed)</span>
                <span className="font-mono">{fmtMoney(stmt.cash_at_end)}</span>
              </div>
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>Cash at end per balance sheet</span>
                <span className="font-mono">{fmtMoney(stmt.cash_at_end_per_balance_sheet)}</span>
              </div>
              <ReconcileBadge stmt={stmt} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function SectionBlock({ section }: { section: CashFlowSection }) {
  return (
    <div className="mb-5">
      <div className="text-sm font-semibold text-slate-800 mb-1 dark:text-slate-200">
        {section.label}
      </div>
      <div className="space-y-0.5 text-sm">
        {section.lines.length === 0 && (
          <div className="text-xs text-slate-400 pl-3 italic dark:text-slate-500">
            No activity in this section.
          </div>
        )}
        {section.lines.map((l, i) => {
          const n = Number(l.amount);
          return (
            <div key={i} className="flex justify-between pl-3">
              <span className="text-slate-700 dark:text-slate-300">{l.label}</span>
              <span className={
                "font-mono " +
                (n < 0 ? "text-red-700" : n > 0 ? "text-slate-900" : "text-slate-400")
              }>
                {fmtMoney(l.amount)}
              </span>
            </div>
          );
        })}
        <div className="flex justify-between border-t border-slate-200 pt-1 mt-1 font-semibold text-slate-900 dark:border-gray-800 dark:text-slate-100">
          <span>Subtotal</span>
          <span className={
            "font-mono " +
            (Number(section.subtotal) < 0 ? "text-red-700" : "text-slate-900")
          }>
            {fmtMoney(section.subtotal)}
          </span>
        </div>
      </div>
    </div>
  );
}


function ReconcileBadge({
  stmt,
}: {
  stmt: { is_reconciled: boolean; cash_at_end: string; cash_at_end_per_balance_sheet: string };
}) {
  const diff = Number(stmt.cash_at_end_per_balance_sheet) - Number(stmt.cash_at_end);
  if (stmt.is_reconciled) {
    return (
      <div className="text-[11px] text-emerald-700 mt-2 italic">
        ✓ Reconciles to balance sheet (within $0.01)
      </div>
    );
  }
  return (
    <div className="text-[11px] text-amber-700 mt-2 italic">
      ⚠ Out of balance by{" "}
      <span className="font-mono">{fmtMoney(String(diff))}</span> —
      check FX revaluation entries or cash-account classification.
    </div>
  );
}
